import { Flex, Loader, Select, SelectProps } from '@mantine/core';
import { ReactNode } from 'react';

export interface CustomSelectProps<T> extends Omit<SelectProps, 'data'> {
  error: ReactNode;
  placeholder: string;
  searchable?: boolean;
  creatable?: boolean;
  isLoading?: boolean;
  data: (T & { label: string; value: string })[];
}

export const CustomAutoComplete = <T,>({
  error,
  placeholder,
  searchable = false,
  creatable = false,
  isLoading = false,
  data,
  ...props
}: CustomSelectProps<T>) => {
  const wrappedGetCreateLabel: SelectProps['getCreateLabel'] = (query) => {
    if (isLoading)
      return (
        <Flex
          justify="center"
          align="center"
          style={{ height: '100%' }}
        >
          <Loader
            size="md"
            color="primary.0"
          />
        </Flex>
      );
    return props.getCreateLabel && props.getCreateLabel(query);
  };

  return (
    <Select
      {...props}
      placeholder={placeholder}
      searchable={searchable}
      creatable={creatable}
      data={data}
      disabled={props?.disabled}
      mt={props.mt ?? { base: 16, md: 24 }}
      radius={8}
      error={error}
      nothingFound={
        isLoading ? (
          <Loader
            size="xl"
            color="primary.0"
          />
        ) : (
          <></>
        )
      }
      getCreateLabel={wrappedGetCreateLabel}
      styles={(theme) => ({
        input: {
          borderColor: error ? `${theme.colors.error[5]} !important` : theme.colors.primary[1],
          borderWidth: 2,
          fontSize: theme.fontSizes.md,
          height: 64,
          color: `${theme.colors.primary[0]} !important`,
          backgroundColor: theme.colors.minor[1],
          padding: 16,

          '&:focus': {
            borderColor: error ? 'inherit' : theme.colors.primary[1],
          },

          '&::placeholder': {
            color: '#2e193e4d !important',
          },
        },
        dropdown: {
          backgroundColor: theme.colors.minor[1],
          border: 'none',
          boxShadow: theme.shadows.md,
          padding: '100px',
          marginTop: -4,
          borderRadius: 4,
        },
        item: {
          padding: 0,
        },
        error: {
          fontSize: theme.fontSizes.sm,
          color: theme.colors.error[5],
          fontWeight: 600,
        },
      })}
    />
  );
};
